import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const MemberActivityListDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"MemberActivityList"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"limit"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"Int"}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"page"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"Int"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"memberActions"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"limit"},"value":{"kind":"Variable","name":{"kind":"Name","value":"limit"}}},{"kind":"Argument","name":{"kind":"Name","value":"memberId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}}},{"kind":"Argument","name":{"kind":"Name","value":"page"},"value":{"kind":"Variable","name":{"kind":"Name","value":"page"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"data"}},{"kind":"Field","name":{"kind":"Name","value":"date"}},{"kind":"Field","name":{"kind":"Name","value":"display"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"entities"}},{"kind":"Field","name":{"kind":"Name","value":"translationKey"}}]}},{"kind":"Field","name":{"kind":"Name","value":"idMemberCreator"}},{"kind":"Field","name":{"kind":"Name","value":"memberCreator"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"nonPublic"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"fullName"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"type"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"MemberActivityList","document":MemberActivityListDocument}} as const;
export type MemberActivityListQueryVariables = Types.Exact<{
  limit?: Types.InputMaybe<Types.Scalars['Int']['input']>;
  memberId: Types.Scalars['ID']['input'];
  page?: Types.InputMaybe<Types.Scalars['Int']['input']>;
}>;


export type MemberActivityListQuery = (
  { __typename: 'Query' }
  & { memberActions: Array<(
    { __typename: 'Action' }
    & Pick<Types.Action, 'id' | 'data' | 'date' | 'idMemberCreator' | 'type'>
    & { display: (
      { __typename: 'Action_Display' }
      & Pick<Types.Action_Display, 'entities' | 'translationKey'>
    ), memberCreator: (
      { __typename: 'Member' }
      & Pick<Types.Member, 'id' | 'fullName'>
      & { nonPublic?: Types.Maybe<(
        { __typename: 'Member_NonPublic' }
        & Pick<Types.Member_NonPublic, 'fullName'>
      )> }
    ) }
  )> }
);

/**
 * __useMemberActivityListQuery__
 *
 * To run a query within a React component, call `useMemberActivityListQuery` and pass it any options that fit your needs.
 * When your component renders, `useMemberActivityListQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMemberActivityListQuery({
 *   variables: {
 *      limit: // value for 'limit'
 *      memberId: // value for 'memberId'
 *      page: // value for 'page'
 *   },
 * });
 */
export function useMemberActivityListQuery(
  baseOptions: TrelloQueryHookOptions<
    MemberActivityListQuery,
    MemberActivityListQueryVariables
  > &
    (
      | { variables: MemberActivityListQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: MemberActivityListDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    MemberActivityListQuery,
    MemberActivityListQueryVariables
  >(MemberActivityListDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useMemberActivityListLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    MemberActivityListQuery,
    MemberActivityListQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    MemberActivityListQuery,
    MemberActivityListQueryVariables
  >(MemberActivityListDocument, options);
}
export function useMemberActivityListSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        MemberActivityListQuery,
        MemberActivityListQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    MemberActivityListQuery,
    MemberActivityListQueryVariables
  >(MemberActivityListDocument, options);
}
export type MemberActivityListQueryHookResult = ReturnType<
  typeof useMemberActivityListQuery
>;
export type MemberActivityListLazyQueryHookResult = ReturnType<
  typeof useMemberActivityListLazyQuery
>;
export type MemberActivityListSuspenseQueryHookResult = ReturnType<
  typeof useMemberActivityListSuspenseQuery
>;
export type MemberActivityListQueryResult = Apollo.QueryResult<
  MemberActivityListQuery,
  MemberActivityListQueryVariables
>;
